import { toast } from 'vue3-toastify';

import type { INotificationCount, INotificationItem, INotificationResponse } from '@/interfaces/agency/notifications';
import notificationService from '@/services/agency/notifications';

export const useNotificationStore = defineStore('notificationStore', {
  state: () => ({
    isLoading: false,
    notificationQuery: {
      limit: 20,
      page: 1,
      type: null as string | null,
      status: null as string | null,
      resourceType: null as string | null,
      dateFrom: null as string | null,
      dateTo: null as string | null
    },
    notifications: [] as INotificationItem[],
    notificationResponse: {} as INotificationResponse,
    unreadCount: {} as INotificationCount,
    selectedNotifications: [] as number[]
  }),

  getters: {
    getNotifications: state => state.notifications || [],

    getLastFiveNotifications: state => {
      const items = state.notifications || [];

      return Array.isArray(items) ? items.slice(0, 5) : [];
    },

    getUnreadCount: state => state.unreadCount?.count || 0
  },

  actions: {
    async fetchNotifications(append = false) {
      // Only the first page swaps the list for a skeleton; appending keeps the rows on screen.
      this.isLoading = !append;
      try {
        const { data } = await notificationService.fetchNotifications(this.notificationQuery);

        this.notificationResponse = data;

        if (append) {
          // Append new notifications to existing ones
          this.notifications = [...this.notifications, ...data.data];
        } else {
          // Replace notifications
          this.notifications = data.data;
        }
      } finally {
        this.isLoading = false;
      }
    },

    async fetchUnreadNotificationCount() {
      const { data } = await notificationService.fetchUnreadNotificationCount();

      this.unreadCount = data;
    },

    async markNotificationAsRead(id: string) {
      await notificationService.markNotificationAsRead(id);

      // Update local state instead of refetching
      const notification = this.notifications.find(n => n.id === id);
      if (notification) {
        notification.isRead = true;
      }

      await this.fetchUnreadNotificationCount();
      toast.success('Notification marked as read!');
    },

    async markAllNotificationsAsRead() {
      await notificationService.markAllNotificationsAsRead();

      // Update all notifications in local state
      this.notifications.forEach(n => {
        n.isRead = true;
      });

      await this.fetchUnreadNotificationCount();
      toast.success('All notifications marked as read!');
    },

    async deleteNotification(id: string) {
      await notificationService.deleteNotification(id);

      // Remove from local state instead of refetching
      this.notifications = this.notifications.filter(n => n.id !== id);

      await this.fetchUnreadNotificationCount();
      toast.success('Notification deleted successfully!');
    },

    async deleteAllNotifications() {
      await notificationService.clearAllNotifications();

      // Clear local state
      this.notifications = [];
      this.notificationQuery.page = 1;

      await this.fetchNotifications();
      await this.fetchUnreadNotificationCount();
      toast.success('Notifications deleted successfully!');
    },

    resetNotificationQuery() {
      this.notificationQuery = {
        limit: 20,
        page: 1,
        type: null,
        status: null,
        resourceType: null,
        dateFrom: null,
        dateTo: null
      };
    }
  }
});
